<template>
  <div class="uk-modal-dialog uk-modal-body uk-margin-auto-vertical form" :style="conversion ? { width: 'unset' } : {}">
    <Form
      v-if="occupations.length && !conversion"
      @submit="onBeforeSubmit"
      :validation-schema="validationSchema"
      ref="form"
      :initial-values="formValues"
    >
      <div class="uk-modal-header form-header">
        <div class="uk-text-bold uk-text-large">
          {{ applicant && applicant.id ? "応募者編集" : "新規追加" }}
        </div>
        <div>
          <ActionButton
            v-if="isEditable && applicant && applicant.id"
            title="従業員に変更"
            color="primary"
            :disabled="!enableConvert.includes(formValues.status) || !isEmpEditable"
            @click="onConvertButtonClick"
            uk-tooltip="title: ステータスを採用か入社に設定すると、従業員に変更できます<br />アセスメントサーベイの受検結果も引き継がれます; pos: top-left; cls: uk-active uk-width-auto;"
          />
        </div>
      </div>
      <div class="uk-modal-body">
        <div class="modal-container">
          <FormError :errors="globalErrors" />
          <div class="field column-2">
            <div>
              <Field name="lastName" v-slot="{ field }">
                <FieldLabel title="姓" :required="true" />
                <input class="uk-input" type="text" v-bind="field" :readonly="!isEditable" />
              </Field>
              <FieldError name="lastName" />
            </div>
            <div>
              <Field name="firstName" v-slot="{ field }">
                <FieldLabel title="名" :required="true" />
                <input class="uk-input" type="text" v-bind="field" :readonly="!isEditable" />
              </Field>
              <FieldError name="firstName" />
            </div>
          </div>
          <div class="field gender">
            <FieldLabel title="性別" />
            <div class="field-item">
              <Field v-slot="{ field }" name="gender" type="radio" value="Male">
                <label>
                  <input type="radio" name="gender" v-bind="field" value="Male" :disabled="!isEditable" />
                  男性
                </label>
              </Field>
              <Field v-slot="{ field }" name="gender" type="radio" value="Female">
                <label>
                  <input type="radio" name="gender" v-bind="field" value="Female" :disabled="!isEditable" />
                  女性
                </label>
              </Field>
              <Field v-slot="{ field }" name="gender" type="radio" value="Unknown">
                <label>
                  <input type="radio" name="gender" v-bind="field" value="Unknown" :disabled="!isEditable" />
                  未回答
                </label>
              </Field>
            </div>
          </div>
          <div class="field">
            <Field name="email" v-slot="{ field }">
              <FieldLabel title="メールアドレス" />
              <input class="uk-input" type="text" v-bind="field" :readonly="!isEditable" />
            </Field>
            <FieldError name="email" />
          </div>
          <div class="field column-2">
            <div>
              <FieldLabel title="雇用形態" :required="true" />
              <Field name="contractType" v-slot="{ field }" v-model="contractEmployee">
                <select class="uk-select" v-bind="field">
                  <option v-for="contract in contractOptions" :key="contract.code" :value="contract.code" :disabled="!isEditable">
                    {{ contract.name }}
                  </option>
                </select>
              </Field>
              <FieldError name="contractType" />
            </div>
            <div class="fade-in" v-show="contractEmployee === 'employee'">
              <FieldLabel title="採用区分" />
              <div class="field-item">
                <Field v-slot="{ field }" name="career" type="radio" value="new">
                  <label>
                    <input type="radio" name="career" v-bind="field" value="new" :disabled="!isEditable" />
                    新卒
                  </label>
                </Field>
                <Field v-slot="{ field }" name="career" type="radio" value="mid">
                  <label>
                    <input type="radio" name="career" v-bind="field" value="mid" :disabled="!isEditable" />
                    中途
                  </label>
                </Field>
              </div>
            </div>
          </div>
          <div class="field">
            <FieldLabel title="ステータス" :required="true" />
            <Field name="status" v-slot="{ field }">
              <select class="uk-select" v-bind="field" @change="onStatusChange">
                <option value="hired" :disabled="!isEditable">採用</option>
                <option value="joined" :disabled="!isEditable">入社</option>
                <option value="not_hired" :disabled="!isEditable">不採用</option>
                <option value="screening" :disabled="!isEditable">選考中</option>
              </select>
            </Field>
          </div>
          <div class="field column-2 bottom_description">
            <div>
              <FieldLabel title="部署" :required="true" />
              <Field name="departmentId" v-slot="{ field }">
                <select class="uk-select" v-bind="field">
                  <option value="" :disabled="!isEditable"></option>
                  <option v-for="department in departments" :key="department.id" :value="department.id" :disabled="!isEditable">
                    {{ department.name }}
                  </option>
                </select>
              </Field>
              <FieldError name="departmentId" />
            </div>
            <div>
              <FieldLabel title="職種" :required="true" />
              <Field name="occupationId" v-slot="{ field }">
                <select class="uk-select" v-bind="field">
                  <option value="" :disabled="!isEditable"></option>
                  <option v-for="occupation in occupations" :key="occupation.id" :value="occupation.id" :disabled="!isEditable">
                    {{ occupation.name }}
                  </option>
                </select>
              </Field>
              <FieldError name="occupationId" />
            </div>
          </div>
          <span>※部署または職種のどちらかは必須です</span>
        </div>
      </div>
      <div class="uk-modal-footer uk-text-right form-action">
        <div>
          <ActionButton title="戻る" class="uk-modal-close" toggleTarget=".applicant-form-modal" />
        </div>
        <div class="form-action-main">
          <ActionButton v-if="isEditable" title="確定" color="primary" type="submit" :disabled="processing" />
          <ActionButton v-if="isEditable && applicant && applicant.id" title="削除" @click="onDeleteButtonClick" color="warn" />
        </div>
      </div>
    </Form>
    <EmployeeFormFields
      v-if="conversion"
      :employee="employee"
      @submitted="onConversionSubmitted"
      formName="応募者"
      :api="convert"
      title="従業員に変更"
    />
  </div>
</template>
<style lang="scss" scoped>
@include modal-form;
.form-header {
  display: flex;
  justify-content: space-between;
}
.bottom_description {
  margin-bottom: 0px;
  & + span {
    display: inline-block;
    margin-bottom: $spacing-medium;
  }
}
.field-item {
  margin-top: $spacing-small;
  margin-bottom: $spacing-small;
}
.fade-in {
  animation-name: fadeIn;
  animation-duration: 0.5s;
  animation-fill-mode: forwards;
  opacity: 0;
}
@keyframes fadeIn {
  0% {
    transform: translateY(30px);
    opacity: 0;
  }
  80% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
<script lang="ts">
import { Options } from "vue-class-component";
import { Emit, Prop, Watch } from "vue-property-decorator";
import FormMixin from "../../helpers/mixins/form";
import {
  ApplicantApi,
  GetApplicantProfile,
  UpdateApplicantRequest,
  Employee,
  CreateEmployee,
  ContractTypeEnum,
  ApplicantStatsEnum
} from "../../autogen";
import { Field, Form } from "vee-validate";
import FieldLabel from "../atoms/FieldLabel.vue";
import * as yup from "yup";
import FieldError from "../atoms/FieldError.vue";
import FormError from "../atoms/FormError.vue";
import ActionButton from "@/components/atoms/ActionButton.vue";
import EmployeeFormFields from "../molecules/EmployeeFormFields.vue";

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  contractType: string;
  career: "new" | "mid";
  status: string;
  departmentId: string;
  occupationId: string;
};
@Options({
  components: {
    Field,
    Form,
    FieldLabel,
    FieldError,
    ActionButton,
    FormError,
    EmployeeFormFields
  }
})
export default class ApplicantForm extends FormMixin {
  departments = this.storeInstance.getDepartments({ withoutAll: true });
  occupations = this.storeInstance.getOccupations();
  @Prop({ type: Object }) applicant?: GetApplicantProfile;
  formName = "応募者";
  formValues: FormValues = {
    firstName: "",
    lastName: "",
    email: "",
    contractType: "employee",
    career: "new",
    status: "screening",
    departmentId: "",
    occupationId: ""
  };
  currentStatus = "screening";
  conversion = false;
  employee?: Partial<Employee>;
  validationSchema!: yup.AnyObjectSchema;
  /** フォーム変更可否フラグ */
  isEditable = false;
  isEmpEditable = false;
  enableConvert = [ApplicantStatsEnum.Hired, ApplicantStatsEnum.Joined];
  contractOptions = Object.entries(ContractTypeEnum).map(([code, name]) => ({ code, name }));
  contractEmployee = "";

  beforeCreate() {
    this.validationSchema = yup.object({
      firstName: yup.string().required(this.$t("input_required", { field: "名" })),
      lastName: yup
        .string()
        .trim()
        .required(this.$t("input_required", { field: "姓" })),
      email: yup
        .string()
        .nullable(true)
        .email(this.$t("input_email")),
      departmentId: yup.string().test("departmentId", this.$t("input_required", { field: "部署か職種" }), function(value) {
        return this.parent.occupationId || value;
      }),
      occupationId: yup.string().test("occupationId", this.$t("input_required", { field: "部署か職種" }), function(value) {
        return this.parent.departmentId || value;
      }),
      contractType: yup.string().required(this.$t("input_required", { field: "雇用形態" }))
    });
  }
  async beforeMount() {
    this.departments = this.storeInstance.getDepartmentHierarchy({ visibilityMyself: true, withoutAll: true })[1];
    if (this.applicant) {
      this.formValues = (this.applicant as unknown) as FormValues;
    }
    this.contractEmployee = this.formValues.contractType;
    this.isEditable = this.$role.isEditable("applicant");
    this.isEmpEditable = this.$role.isEditable("employee");
  }
  async onBeforeSubmit(values: FormValues) {
    await this.onSubmit(async () => {
      Object.assign(values, {
        career: ["employee"].includes(values.contractType as string) ? values.career : ""
      });
      if (this.applicant) {
        await new ApplicantApi().updateApplicant({
          applicantId: this.applicant!.id,
          updateApplicantRequest: values as UpdateApplicantRequest
        });
      } else {
        await new ApplicantApi().createApplicant({
          createApplicantRequest: values as UpdateApplicantRequest
        });
      }
      this.$emit("submitted");
    });
  }
  async onDeleteButtonClick() {
    this.onDelete(async () => {
      await new ApplicantApi().deleteApplicant({
        applicantId: this.applicant!.id
      });
    });
  }
  async convert(values: CreateEmployee) {
    await new ApplicantApi().toEmployee({
      applicantId: this.applicant!.id,
      body: values
    });
  }
  @Emit("submitted")
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onConversionSubmitted() {}
  onConvertButtonClick() {
    if (!this.isEditable && !this.isEmpEditable) return;
    this.conversion = true;
    this.employee = Object.assign({}, {
      lastName: this.applicant?.lastName,
      firstName: this.applicant?.firstName,
      gender: this.applicant?.gender,
      email: this.applicant?.email,
      contractType: this.applicant?.contractType,
      career: this.applicant?.career,
      enrollment: "enrolled",
      departmentId: this.applicant!.department?.id,
      occupationId: this.applicant!.occupation?.id
    } as Employee);
  }
  onStatusChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.currentStatus = target.value;
  }
  @Watch("storeInstance.state.departments")
  stateDepartmentsChange() {
    this.departments = this.storeInstance.getDepartmentHierarchy({ visibilityMyself: true, withoutAll: true })[1];
  }
}
</script>
